import React, { useEffect, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Sparkles, HelpCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Phase 13.1 — Onboarding tour, redesigned post-pivot.
 *
 * 6 steps (1 conditional). All steps display in a clean navy card with amber
 * accents, progress dots, ESC/backdrop dismiss, and a smooth spotlight ring
 * around the targeted dashboard element. Last step persists `tour_completed`
 * server-side via /users/me/onboarding-dismissed.
 *
 * `<OnboardingTour />` mounts on the Dashboard. The "Replay tour" button on
 * the dashboard sets `localStorage.fc_force_tour = "1"` then reloads — that
 * key bypasses the dismissed gate exactly once.
 */

const FORCE_KEY = "fc_force_tour";

function buildSteps(user) {
  const first = user?.first_name || (user?.name || "").split(" ")[0] || "there";
  const isOrgPriv = ["org_owner", "super_admin", "executive"].includes(user?.role || "");
  return [
    {
      key: "welcome",
      title: `Welcome to Freddy Compute Academy, ${first}`,
      body: "You've got complimentary access during early access. Live AI training events run weekly — the community and courses are growing right behind them.",
      selector: null, // centered modal, no spotlight
    },
    {
      key: "live-events",
      title: "Reserve your seat at the next live event",
      body: "These are live operator sessions. Click Join Meeting on any upcoming card to join — or add it to your calendar so you don't miss it.",
      selector: '[data-testid="dashboard-live-events"]',
    },
    {
      key: "community",
      title: "Meet the community",
      body: "Share wins, ask questions, learn from other operators. New posts every day.",
      selector: '[data-side-nav="community"], [href="/community"], [data-testid="dashboard-community-feed"]',
    },
    {
      key: "courses",
      title: "Courses launching soon",
      body: "AI Business 101 and Vibecoding 101 are live with intro videos. The full catalog drops in waves — join the waitlist on any Coming Soon course to be first.",
      selector: '[data-side-nav="my-courses"], [href="/courses"], [href="/dashboard/my-courses"]',
    },
    {
      key: "team",
      title: "Bring your team along",
      body: "Invite teammates from your organization — no seat limits during early access.",
      selector: '[data-side-nav="org"], [href*="/org"], [data-testid^="invite-team"]',
      visible: isOrgPriv,
    },
    {
      key: "done",
      title: `You're all set, ${first}`,
      body: "Tap the help icon any time to revisit this tour. Now go reserve your seat.",
      selector: null,
      isFinal: true,
    },
  ].filter((s) => s.visible !== false);
}

export default function OnboardingTour() {
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [pos, setPos] = useState(null);
  const [visible, setVisible] = useState(false);
  const [fading, setFading] = useState(false);

  const steps = buildSteps(user || {});

  // Decide whether to show the tour.
  useEffect(() => {
    if (!user) return;
    const forced = typeof window !== "undefined" && localStorage.getItem(FORCE_KEY) === "1";
    if (!forced) {
      if (user.onboarding_dismissed_at) return;
      if ((user.total_xp || 0) > 0) return;
      if (user.is_impersonated) return;
    }
    setStep(0);
    setVisible(true);
    if (forced) localStorage.removeItem(FORCE_KEY);
  }, [user]);

  // Compute spotlight position whenever the step or window changes.
  useEffect(() => {
    if (!visible) return;
    const s = steps[step];
    if (!s?.selector) { setPos(null); return; }
    const find = () => {
      for (const sel of s.selector.split(", ")) {
        const el = document.querySelector(sel);
        if (el) {
          const r = el.getBoundingClientRect();
          setPos({
            top: r.top + window.scrollY,
            left: r.left + window.scrollX,
            width: r.width, height: r.height,
          });
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
      }
      setPos(null);
    };
    find();
    const t1 = setTimeout(find, 350);
    const t2 = setTimeout(find, 800);
    const onResize = () => find();
    window.addEventListener("resize", onResize);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", onResize); };
  }, [step, visible, steps]);

  const dismiss = useCallback(async () => {
    setVisible(false);
    try {
      await api.post("/users/me/onboarding-dismissed");
      setUser({ ...(user || {}), onboarding_dismissed_at: new Date().toISOString() });
    } catch (e) {
      console.error("dismiss onboarding", e);
    }
  }, [user, setUser]);

  // ESC key + backdrop click → dismiss
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  const goTo = (delta) => {
    setFading(true);
    setTimeout(() => {
      setStep((s) => Math.max(0, Math.min(steps.length - 1, s + delta)));
      setFading(false);
    }, 180);
  };
  const next = () => (step >= steps.length - 1 ? dismiss() : goTo(1));
  const back = () => goTo(-1);

  if (!visible || !steps.length) return null;
  const s = steps[step];
  const hasSpotlight = !!pos;

  // Card placement: under the target with room, else centered on screen.
  const cardStyle = hasSpotlight && pos.top + pos.height + 260 < window.innerHeight + window.scrollY
    ? { top: pos.top + pos.height + 18, left: Math.max(20, Math.min(window.innerWidth - 440 - 20, pos.left)) }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

  return (
    <>
      {/* Dim backdrop (clickable → skip) */}
      <div
        onClick={dismiss}
        className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[1px] transition-opacity duration-200"
        data-testid="onboarding-backdrop"
      />

      {/* Spotlight ring around target */}
      {hasSpotlight && (
        <div
          className="pointer-events-none fixed z-[91] rounded-lg transition-all duration-300 ease-out"
          style={{
            top: pos.top - 6,
            left: pos.left - 6,
            width: pos.width + 12,
            height: pos.height + 12,
            outline: "2px solid #FBBF24",
            outlineOffset: "4px",
            boxShadow: "0 0 0 9999px rgba(6,10,36,0.55), 0 0 40px rgba(251,191,36,0.18)",
          }}
          data-testid="onboarding-spotlight"
        />
      )}

      {/* Card */}
      <div
        className={`fixed z-[95] w-[92vw] max-w-[420px] bg-white border border-gray-200 rounded-2xl shadow-2xl shadow-black/40 p-7 transition-opacity duration-200 ${fading ? "opacity-0" : "opacity-100"}`}
        style={cardStyle}
        data-testid="onboarding-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress dots + label */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`rounded-full transition-all duration-300 ${i === step ? "w-5 h-1.5 bg-amber-400" : "w-1.5 h-1.5 bg-white/20"}`}
              />
            ))}
            <span className="ml-2 text-[10px] tracking-[0.22em] uppercase font-bold text-gray-500">
              Step {step + 1} of {steps.length}
            </span>
          </div>
          <button
            onClick={dismiss}
            className="text-gray-400 hover:text-gray-900 transition-colors"
            title="Skip tour"
            data-testid="onboarding-dismiss"
          >
            <X size={15} />
          </button>
        </div>

        {/* Sparkle accent */}
        <Sparkles size={16} className="text-amber-300 mb-3" />

        <h3 className="text-xl font-black tracking-tight mb-3 text-white leading-tight" data-testid={`tour-title-${s.key}`}>
          {s.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-7">
          {s.body}
        </p>

        <div className="flex items-center justify-between gap-3">
          <button
            onClick={dismiss}
            className="text-xs text-gray-500 hover:text-gray-900 transition-colors tracking-wider"
            data-testid="onboarding-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={back}
                className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold tracking-[0.16em] uppercase border border-gray-200 text-gray-600 hover:border-white/45 hover:text-gray-900 transition-colors rounded"
                data-testid="onboarding-back"
              >
                <ArrowLeft size={12} /> Back
              </button>
            )}
            <button
              onClick={next}
              className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-black tracking-[0.18em] uppercase bg-amber-400 hover:bg-amber-300 text-fc-navy transition-colors rounded"
              data-testid="onboarding-next"
            >
              {s.isFinal ? "Got it — let's go" : "Next"}
              {!s.isFinal && <ArrowRight size={12} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Small dashboard-header button to relaunch the tour. */
export function ReplayTourButton({ className = "" }) {
  const replay = () => {
    localStorage.setItem(FORCE_KEY, "1");
    window.location.reload();
  };
  return (
    <button
      onClick={replay}
      className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500 hover:text-amber-300 transition-colors ${className}`}
      data-testid="replay-tour-btn"
      title="Replay onboarding tour"
    >
      <HelpCircle size={11} /> Replay Tour
    </button>
  );
}
